export const STORY_METRIC_KEYS = [
  "sourceLoc",
  "ccAbove",
  "cognitiveAbove",
  "largeFunctions",
  "cloneGroups",
  "duplicationPct",
  "ccPerKloc",
  "cognitivePerKloc",
] as const;

const LABELS: Record<string, string> = {
  sourceLoc: "Source LOC",
  testLoc: "Test LOC",
  fileCount: "Source files",
  functions: "Measured functions",
  functionCount: "Measured functions",
  ccAbove: "High cyclomatic complexity",
  cognitiveAbove: "High cognitive complexity",
  largeFunctions: "Large functions",
  cloneGroups: "Duplicate groups",
  duplicatedLines: "Duplicated lines",
  duplicationPct: "Duplicated share",
  ccPerKloc: "Cyclomatic per 1k LOC",
  cognitivePerKloc: "Cognitive per 1k LOC",
  ccAbovePct: "High cyclomatic share",
  cognitiveAbovePct: "High cognitive share",
  largeFunctionsPct: "Large function share",
  p95Cyclomatic: "Cyclomatic p95",
  p50Cyclomatic: "Cyclomatic median",
  maxCyclomatic: "Cyclomatic maximum",
  compilerErrors: "Compiler errors",
  oxlintWarnings: "Lint warnings",
  oxlintErrors: "Lint errors",
  reactDoctorFindings: "React diagnostics",
  qltyCodeSmells: "Qlty code smells",
  qltySmells: "Qlty all smells",
  knipUnusedFiles: "Unused files",
  knipUnusedExports: "Unused exports",
  knipUnusedDeps: "Unused dependencies",
  depcruiseCycles: "Dependency Cruiser cycles",
  fallowCycles: "Fallow cycles",
};

export function metricLabel(key: string): string {
  return LABELS[key] ?? key;
}

export function formatMetric(value: number | null | undefined, key?: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (key === "ms" || key === "span" || key === "recordedSpanMs") return formatDurationMs(value);
  if (key && (key.endsWith("Pct") || key === "duplicationPct" || key === "percent")) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
  }
  if (key && (key.endsWith("PerKloc") || key === "per1k")) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);
  }
  if (key === "tokens" || (Math.abs(value) >= 10_000 && Number.isInteger(value) && key !== "sourceLoc" && key !== "testLoc" && key !== "duplicatedLines")) {
    return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(value);
  }
  const maxFrac = Number.isInteger(value) ? 0 : Math.abs(value) >= 100 ? 1 : 2;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: maxFrac }).format(value);
}

/** Preserve exact data while avoiding a directional sign on a rounded zero. */
export function formatMetricChange(value: number | null | undefined, key?: string): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const magnitude = formatMetric(Math.abs(value), key);
  if (/^0(?:[.,]0+)?%?$/.test(magnitude)) return `${value === 0 ? "" : "≈"}${magnitude}`;
  return `${value > 0 ? "+" : value < 0 ? "−" : ""}${magnitude}`;
}

export function formatDurationMs(ms: number): string {
  const abs = Math.abs(ms);
  const signed = (n: number, unit: string) =>
    `${new Intl.NumberFormat(undefined, { maximumFractionDigits: n >= 10 ? 0 : 1 }).format(n)}${unit}`;
  if (abs >= 86_400_000) return signed(ms / 86_400_000, "d");
  if (abs >= 3_600_000) return signed(ms / 3_600_000, "h");
  if (abs >= 60_000) return signed(ms / 60_000, "m");
  if (abs >= 1_000) return signed(ms / 1_000, "s");
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(ms)} ms`;
}

export function pickStoryMetrics<T extends { key: string; absolute: number | null }>(metrics: T[], limit = 8): T[] {
  const byKey = new Map(metrics.map((row) => [row.key, row]));
  const picked: T[] = [];
  for (const key of STORY_METRIC_KEYS) {
    const row = byKey.get(key);
    if (row && row.absolute !== null) picked.push(row);
  }
  for (const row of metrics) {
    if (picked.length >= limit) break;
    if (picked.includes(row) || row.absolute === null || row.key.endsWith("Pct")) continue;
    picked.push(row);
  }
  return picked.slice(0, limit);
}
